import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import { useI18n } from "@nelurio/i18n";
import { api } from "../api/client";

interface SearchResults {
  courses: Array<{ id: string; title: string }>;
  tasks: Array<{ id: string; title: string; dueDate: string; kind: string }>;
  notes: Array<{ id: string; courseId: string; courseTitle: string; snippet: string }>;
  flashcards: Array<{ id: string; courseId: string; courseTitle: string; front: string; back: string }>;
  resources: Array<{ id: string; courseId: string; courseTitle: string; title: string }>;
  threads: Array<{ id: string; title: string }>;
}

const EMPTY: SearchResults = { courses: [], tasks: [], notes: [], flashcards: [], resources: [], threads: [] };

/** Header global search: one query across all user content, grouped results. */
export function GlobalSearchOverlay({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    if (!open || query.trim().length < 2) {
      setResults(null);
      setFailed(false);
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setFailed(false);
      try {
        const data = await api<{ results: SearchResults }>(`/search?q=${encodeURIComponent(query.trim())}`, { signal: controller.signal });
        setResults(data.results);
      } catch (error) {
        if ((error as { name?: string })?.name !== "AbortError") setFailed(true);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query, open]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && open) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const total = results ? Object.values(results).reduce((sum, list) => sum + (list as unknown[]).length, 0) : 0;

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  const row = (key: string, label: string, snippet: string | null, path: string) => (
    <button type="button" className="gsearch__row" key={key} onClick={() => go(path)}>
      <strong>{label}</strong>
      {snippet && <small>{snippet}</small>}
    </button>
  );

  return createPortal(
    <div className="gsearch-backdrop" onClick={onClose} role="presentation">
      <div className="gsearch" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <input
          ref={inputRef}
          className="gsearch__input"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("common.search")}
          autoFocus
        />
        <div className="gsearch__body">
          {failed ? (
            <p className="gsearch__empty">{t("assistant.error")}</p>
          ) : query.trim().length < 2 ? (
            <p className="gsearch__empty">{t("search.minChars")}</p>
          ) : loading ? (
            <p className="gsearch__empty">{t("ai.thinking")}</p>
          ) : results && total === 0 ? (
            <p className="gsearch__empty">{t("search.noResults")}</p>
          ) : results ? (
            <>
              {results.courses.length > 0 && (
                <section><h3>{t("nav.courses")}</h3>
                  {results.courses.map((item) => row(item.id, item.title, "", `/courses/${item.id}`))}
                </section>
              )}
              {results.tasks.length > 0 && (
                <section><h3>{t("nav.tasks")}</h3>
                  {results.tasks.map((item) => row(`t-${item.id}`, item.title, item.dueDate, "/tasks"))}
                </section>
              )}
              {results.notes.length > 0 && (
                <section><h3>{t("nav.library")}</h3>
                  {results.notes.map((item) => row(`n-${item.id}`, item.courseTitle, item.snippet, `/courses/${item.courseId}`))}
                </section>
              )}
              {results.flashcards.length > 0 && (
                <section><h3>{t("study.flashcards")}</h3>
                  {results.flashcards.map((item) => row(`f-${item.id}`, item.front, item.back, `/courses/${item.courseId}`))}
                </section>
              )}
              {results.resources.length > 0 && (
                <section><h3>{t("nav.library")}</h3>
                  {results.resources.map((item) => row(`r-${item.id}`, item.title, item.courseTitle, "/library"))}
                </section>
              )}
              {results.threads.length > 0 && (
                <section><h3>{t("assistant.history")}</h3>
                  {results.threads.map((item) => row(`th-${item.id}`, item.title, "", "/assistant"))}
                </section>
              )}
            </>
          ) : null}
        </div>
        <p className="gsearch__hint">{t("search.hint")}</p>
      </div>
    </div>,
    document.body
  );
}
